import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import StoreFront from "@/pages/store/StoreFront";
import ProductDetail from "@/pages/store/ProductDetail";
import Cart from "@/pages/store/Cart";
import Checkout from "@/pages/store/Checkout";
import OrderStatus from "@/pages/store/OrderStatus";
import Login from "@/pages/app/Login";
import Register from "@/pages/app/Register";
import Dashboard from "@/pages/app/Dashboard";
import Orders from "@/pages/app/Orders";
import Menu from "@/pages/app/Menu";
import Stock from "@/pages/app/Stock";
import Financial from "@/pages/app/Financial";
import Customers from "@/pages/app/Customers";
import Settings from "@/pages/app/Settings";
import Onboarding from "@/pages/app/Onboarding";
import Promotions from "@/pages/app/Promotions";
import POS from "@/pages/app/POS";
import OnlineSales from "@/pages/app/OnlineSales";
import PrintStation from "@/pages/app/PrintStation";
import Support from "@/pages/app/Support";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminStores from "@/pages/admin/AdminStores";
import AdminTemplates from "@/pages/admin/AdminTemplates";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import LandingPage from "@/pages/LandingPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const WS_URL = BACKEND_URL?.replace('https', 'wss').replace('http', 'ws') + '/api';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      console.error('Auth error:', e);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user } = res.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Context
const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : { items: [], storeSlug: null };
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addItem = (item, storeSlug) => {
    setCart(prev => {
      // If different store, clear cart
      if (prev.storeSlug && prev.storeSlug !== storeSlug) {
        return { items: [item], storeSlug };
      }
      
      // Check if item exists
      const existingIndex = prev.items.findIndex(
        i => i.product_id === item.product_id && 
             i.variant_id === item.variant_id &&
             JSON.stringify(i.addons) === JSON.stringify(item.addons)
      );
      
      if (existingIndex >= 0) {
        const newItems = [...prev.items];
        newItems[existingIndex].quantity += item.quantity;
        return { ...prev, items: newItems };
      }
      
      return { items: [...prev.items, item], storeSlug };
    });
    toast.success('Item adicionado ao carrinho');
  };

  const updateQuantity = (index, quantity) => {
    setCart(prev => {
      const newItems = [...prev.items];
      if (quantity <= 0) {
        newItems.splice(index, 1);
      } else {
        newItems[index].quantity = quantity;
      }
      return { ...prev, items: newItems };
    });
  };

  const removeItem = (index) => {
    setCart(prev => {
      const newItems = [...prev.items];
      newItems.splice(index, 1);
      return { ...prev, items: newItems };
    });
  };

  const clearCart = () => {
    setCart({ items: [], storeSlug: null });
    localStorage.removeItem('cart');
  };

  const getTotal = () => {
    return cart.items.reduce((sum, item) => {
      const itemTotal = item.unit_price * item.quantity;
      const addonsTotal = (item.addons || []).reduce((a, addon) => a + addon.price * addon.quantity, 0);
      return sum + itemTotal + addonsTotal;
    }, 0);
  };

  const getItemCount = () => {
    return cart.items.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{ cart, addItem, updateQuantity, removeItem, clearCart, getTotal, getItemCount }}>
      {children}
    </CartContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/app/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <div className="App min-h-screen bg-[#1a1a2e]">
            <Routes>
              {/* Landing Page */}
              <Route path="/" element={<LandingPage />} />
              
              {/* Public Store Routes */}
              <Route path="/:slug" element={<StoreFront />} />
              <Route path="/:slug/produto/:productId" element={<ProductDetail />} />
              <Route path="/:slug/carrinho" element={<Cart />} />
              <Route path="/:slug/checkout" element={<Checkout />} />
              <Route path="/:slug/pedido/:orderId" element={<OrderStatus />} />
              
              {/* Store Admin Routes */}
              <Route path="/app/login" element={<Login />} />
              <Route path="/app/register" element={<Register />} />
              <Route path="/app/onboarding" element={
                <ProtectedRoute allowedRoles={['store_admin']}>
                  <Onboarding />
                </ProtectedRoute>
              } />
              <Route path="/app/dashboard" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/app/pedidos" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <Orders />
                </ProtectedRoute>
              } />
              <Route path="/app/impressao" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <PrintStation />
                </ProtectedRoute>
              } />
              <Route path="/app/cardapio" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <Menu />
                </ProtectedRoute>
              } />
              <Route path="/app/estoque" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <Stock />
                </ProtectedRoute>
              } />
              <Route path="/app/financeiro" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <Financial />
                </ProtectedRoute>
              } />
              <Route path="/app/clientes" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <Customers />
                </ProtectedRoute>
              } />
              <Route path="/app/configuracoes" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/app/promocoes" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <Promotions />
                </ProtectedRoute>
              } />
              <Route path="/app/pdv" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <POS />
                </ProtectedRoute>
              } />
              <Route path="/app/venda-online" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <OnlineSales />
                </ProtectedRoute>
              } />
              <Route path="/app/suporte" element={
                <ProtectedRoute allowedRoles={['store_admin', 'super_admin']}>
                  <Support />
                </ProtectedRoute>
              } />
              
              {/* Super Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/suporte" element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminSupport />
                </ProtectedRoute>
              } />
              <Route path="/admin/notificacoes" element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminNotifications />
                </ProtectedRoute>
              } />
              <Route path="/admin/lojas" element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminStores />
                </ProtectedRoute>
              } />
              <Route path="/admin/templates" element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminTemplates />
                </ProtectedRoute>
              } />
              <Route path="/admin/usuarios" element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminUsers />
                </ProtectedRoute>
              } />
              
              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </div>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
